import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Avatar from "../NumberOptions/Avatar";
import api from "../../services/axios/index";
import IconTickets from "../SvgIcons/IconTickets";
import { Link } from "react-router-dom";
import IconInformation from "../SvgIcons/IconInformation";

function FaqSidebar({ customerCode, agentsCompany }) {
  const [openIndexes, setOpenIndexes] = useState([0, 1]);
  const [recentTickets, setRecentTickets] = useState([]);
  const [contactDetails, setContactDetails] = useState({});
  const [showFullContact, setShowFullContact] = useState(false);
  const [loading, setLoading] = useState(true);

  const toggleFAQ = (index) => {
    setOpenIndexes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch recent tickets
        const ticketRes = await api.post("/recent_tickets", {
          Customer_Code: customerCode,
          Agents_Company: agentsCompany
        });

        function formatDateTime(dateStr) {
          const date = new Date(dateStr);

          const dd = String(date.getDate()).padStart(2, "0");
          const MM = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
          const yyyy = date.getFullYear();

          const HH = String(date.getHours()).padStart(2, "0");
          const mm = String(date.getMinutes()).padStart(2, "0");
          const ss = String(date.getSeconds()).padStart(2, "0");

          return `${dd}-${MM}-${yyyy} ${HH}:${mm}:${ss}`;
        }

        const ticketData = ticketRes.data.data || [];

        const tickets = ticketData
          .sort((a, b) => new Date(b.Create_Date_Time_Stamp) - new Date(a.Create_Date_Time_Stamp)) // Sort DESC
          .slice(0, 5) // Take the most recent 5
          .map((item) => ({
            TicketNo: item.Ticket_No,
            // date: new Date(item.Create_Date_Time_Stamp).toLocaleDateString(),
            date: formatDateTime(item.Create_Date_Time_Stamp),
            Status_DisplayName: item.Status_DisplayName || item.Status_Name,
            Status: item.Status_Name || item.Status_DisplayName,
            Priority:
              item.Priority_DisplayName || item.Priority_Name || "Medium",
            PriorityColor: item.Priority_Color || "#808080",
          }));

        setRecentTickets(tickets);

        // Fetch customer details
        const customerRes = customerCode ? await api.post(`/view_customer/${customerCode}`) : '';
        const customer = customerRes.data?.data;

        if (customer) {
          setContactDetails({
            Name: customer.Customer_Name || "N/A",
            Email: customer.Contact_Email_Id || "N/A",
            Mobile: customer.Contact_Mobile_No || "N/A",
            Address: customer.Address || "N/A",
            City: customer.City || "N/A",
            State: customer.State || "N/A",
            "Serial no.": customer.Serial_No || "N/A",
            "Customer type": customer.Customer_Type || "N/A",
          });
        } else {
          setContactDetails({ Message: "User not found" });
        }
      } catch (err) {
        console.error("Error fetching data:", err.message);
        setContactDetails({ Message: "User not found" });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [customerCode]);

  const faqs = [
    {
      question: "Contact Details",
      answer: contactDetails,
      avatar: contactDetails?.Name || "User",
    },
    {
      question: "Recent Tickets",
      answer: recentTickets,
      avatar: null,
    },
  ];

  return (
    <div className="faqsidebar">
      {faqs.map((faq, index) => (
        <div key={index} className="detailsbox">
          <div
            className={`cursor-pointer d-flex justify-content-between align-items-center ${openIndexes.includes(index) ? "openfaq" : "closefaq"
              }`}
            onClick={() => toggleFAQ(index)}
          >
            <h6 className="mb-0 d-flex align-items-center gap-2">
              {faq.avatar ? (
                <Avatar
                  name={faq.avatar}
                  index={index}
                  width={25}
                  height={25}
                  fontSize={12}
                />
              ) : (
                <IconTickets />
              )}

              {faq.question}
            </h6>
            <span>{openIndexes.includes(index) ? "−" : "+"}</span>
          </div>

          <AnimatePresence>
            {openIndexes.includes(index) && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden textdata"
              >
                {loading && index === 1 ? (
                  <p>Loading...</p>
                ) : Array.isArray(faq.answer) ? (
                  faq.answer.length === 0 ? (
                    <ul className="list-disc pl-5">
                      <li>No recent tickets found.</li>
                    </ul>
                  ) : (
                    <ul className="list-disc pl-5">
                      {faq.answer.map((ticket, idx) => (
                        <li key={idx}>
                          <div className="ticketno">
                            <strong>Ticket No:</strong> {ticket.TicketNo}
                          </div>
                          <div className="ticketno">
                            <strong>Date:</strong> {ticket.date}
                          </div>
                          <div className="ticketno">
                            <strong>Status:</strong>&nbsp;
                            <Link
                              to={`/view-ticket-history/${ticket.TicketNo}`}
                              className={`statusbtn ${ticket.Status.toLowerCase()}`}
                            >
                              {ticket.Status_DisplayName}
                            </Link>
                          </div>
                          <div className="ticketno">
                            <strong>Priority:</strong>{" "}
                            <span
                              className="priority-icon"
                              style={{
                                backgroundColor: ticket.PriorityColor,
                                display: "inline-block",
                                width: "10px",
                                height: "10px",
                                borderRadius: "50%",
                                marginRight: "5px",
                              }}
                            ></span>
                            {ticket.Priority}
                          </div>
                          <hr className="my-2 border-gray-200" />
                        </li>
                      ))}
                    </ul>
                  )
                ) : (
                  <ul className="list-disc pl-5">
                    {faq.answer?.Message === "User not found" ? (
                      <li>User not found</li>
                    ) : (
                      <>
                        <li>
                          <div className="ticketno">
                            <strong>Name:</strong> {faq.answer.Name || "N/A"}
                          </div>
                        </li>
                        <li>
                          <div className="ticketno">
                            <strong>Email:</strong> {faq.answer.Email || "N/A"}
                          </div>
                        </li>

                        {showFullContact &&
                          Object.entries(faq.answer).map(([key, value]) => {
                            if (["Name", "Email"].includes(key)) return null;
                            return (
                              <li key={key}>
                                <div className="ticketno">
                                  <strong>{key}:</strong> {value || "N/A"}
                                </div>
                              </li>
                            );
                          })}

                        <li>
                          <div
                            className="viewmore border-rounded text-center cursor-pointer"
                            onClick={() => setShowFullContact((prev) => !prev)}
                          >
                            <IconInformation />  {showFullContact ? 'View less info' : 'View more info'}
                          </div>
                        </li>
                      </>
                    )}
                  </ul>
                )}
              </motion.div>
            )}
          </AnimatePresence>

        </div>
      ))}
    </div>
  );
}

export default FaqSidebar;
